// Ported from files/snapdraw-design-kit/code/src/lib/i18n/strings/account.ts.
// The kit's own dictionary used two bare, un-namespaced keys ("title",
// "subtitle") that happen not to collide with any other namespace today but
// are one added key away from silently doing so — renamed to
// "account.title"/"account.subtitle" here. plan.name/plan.price/plan.renews
// were fixture values ("Pro Crew $39/mo") that don't match this app's real
// PLAN_NAME/PLAN_PRICE_HINT/renewal date (AccountPage renders those directly
// rather than through the dictionary); plan.body is replaced with our real
// PLAN_BLURB text, and free-trial state (not a kit scenario) gets its own
// keys.
// 2026-08-24 (DESIGN-SPEC.md §13): "Signed in as" and "Personal info" merged
// into one "You" card (personal.* renamed to you.*, plus a merged
// you.error.generic replacing the shared personal.error.generic the two
// separate save handlers used to reuse); added storage.title/storage.help for
// the new placeholder Storage card (the meter line itself reuses common.ts's
// existing shell.storageUsed — no new key needed there); business.help/
// business.saved copy updated to match the new spec; added
// business.error.generic (previously shared personal.error.generic too).
// Later same day: the Company field was removed from the You card entirely
// (business name only lives on Business Profile — see AccountPage.tsx),
// which dropped signedIn.company. Then: signedIn.email/business.email were
// both bare "Email", which read as duplicated/ambiguous once both cards sit
// next to each other — signedIn.email is now "Login email" and
// business.email is "Billing email" (the one that prints on documents).
// Never label both simply "Email". Same reasoning applied to the two phone
// fields once the design kit's own docs were reconciled against this file
// (the source of truth): you.phone is "Your phone" (was "Phone number") and
// business.phone is "Business phone" (was bare "Phone") — never label both
// simply "Phone" either.
export const en = {
  "account.breadcrumb": "Settings · Account",
  "account.title": "Account",
  "account.subtitle": "Your identity, plan and data. Changes here affect every document you send.",
  "you.title": "You",
  "you.help": "How you sign in and how the app addresses you. Not printed on documents.",
  "signedIn.email": "Login email",
  "signedIn.manageBilling": "Manage billing",
  "signedIn.logOut": "Log out",
  "you.fullName": "Full name",
  "you.fullNamePlaceholder": "Bob Builder",
  "you.phone": "Your phone",
  "you.phonePlaceholder": "(214) 555-0192",
  "you.phoneType": "Phone type",
  "you.phoneType.placeholder": "Select type",
  "you.phoneType.mobile": "Mobile",
  "you.phoneType.office": "Office",
  "you.phoneType.home": "Home",
  "you.save": "Save your info",
  "you.saved": "Your info saved",
  "you.error.generic": "Could not save your info.",
  "business.title": "Business Profile",
  "business.help": "This is what prints at the top of every quote and pay application. Quotes prefill from here.",
  "business.name": "Business name",
  "business.license": "License #",
  "business.phone": "Business phone",
  "business.email": "Billing email",
  "business.street": "Street address",
  "business.city": "City",
  "business.state": "State",
  "business.zip": "ZIP",
  "business.save": "Save business info",
  "business.saved": "Business profile saved",
  "business.error.generic": "Could not save your business profile.",
  "business.logo.label": "Business logo",
  "business.logo.dropPrompt": "Drop a PNG here, or",
  "business.logo.chooseFile": "Choose file",
  "business.logo.hint": "Transparent PNG works best. It prints at the top-right of every document.",
  "business.logo.pngOnly": "PNG only",
  "business.logo.replace": "Replace",
  "business.logo.remove": "Remove",
  "business.logo.removed": "Logo removed",
  "business.logo.uploading": "Uploading {filename}",
  "business.logo.error.notPng": "PNG only — JPGs can't carry a transparent background.",
  "business.logo.error.tooLarge": "That file is over 2 MB. Try exporting it smaller.",
  "business.logo.error.uploadFailed": "Upload didn't finish. Check your signal and try again.",
  "business.logo.retry": "Try again",
  "markupDefault.title": "Quoting defaults",
  "markupDefault.help": "The materials markup % every new quote starts with, unless a client has their own saved rate.",
  "markupDefault.label": "Default markup %",
  "markupDefault.save": "Save default",
  "markupDefault.saved": "Default markup saved",
  "markupDefault.error.generic": "Could not save your default markup.",
  "storage.title": "Storage",
  "storage.help": "Everything is synced. Old documents stay readable even after you hit the cap.",
  "export.title": "Export your data",
  "export.help": "Download everything on your account — documents, clients, contracts, catalog, and templates — as one file.",
  "export.cta": "Download my data",
  "export.exporting": "Exporting…",
  "export.error.generic": "Could not export your data. Please try again.",
  "plan.title": "Plan",
  "plan.body": "Unlimited quotes and G702/G703 payment applications — up to 3 user accounts.",
  "plan.freeTrial": "Free Trial",
  "plan.daysLeftSingular": "{count} day left",
  "plan.daysLeftPlural": "{count} days left",
  "plan.subscribeButton": "Subscribe — {price}",
  "plan.discountPercent": "{percent}% OFF",
  "plan.discountAmount": "{amount} OFF",
  "plan.discountEndsOn": "Discount ends {date}",
  "plan.discountEndsRenews": "Discount ends {date} — renews at {price}",
  "plan.renews": "Renews {date}",
  "plan.cancelledAccessThrough": "Cancelled — access through {date}",
  "plan.ownerOnly": "Ask your account owner to manage billing.",
  "language.title": "Language",
  "language.help": "Saved to your profile and applied on every screen and PDF header.",
  "theme.title": "Appearance",
  "theme.dark": "Dark",
  "theme.light": "Light",
  "theme.system": "System",
  "theme.help": "Yours only — saved to this device, not shared with your team. Printed quotes and pay applications stay black on white either way.",
  "team.title": "Team",
  "team.help": "Up to 3 people can share this account — invite teammates by email.",
  "team.you": "(You)",
  "team.role.owner": "Owner",
  "team.role.member": "Member",
  "team.removeButton": "Remove",
  "team.removeConfirm": "Remove {email} from your team?",
  "team.pendingTitle": "Pending invites",
  "team.pendingBadge": "Pending",
  "team.expiredBadge": "Expired",
  "team.cancelButton": "Cancel",
  "team.cancelConfirm": "Cancel the invite to {email}?",
  "team.capReached": "Your account has reached its 3-member limit.",
  "team.inviteLabel": "Invite by email",
  "team.invitePlaceholder": "teammate@example.com",
  "team.inviteButton": "Send invite",
  "team.inviteSent": "Invite sent",
  "team.inviteSentNoEmail": "Invite created, but the email couldn't be sent — share the sign-up link with them directly.",
  "team.error.generic": "Something went wrong. Try again.",
  "danger.title": "Danger zone",
  "danger.body": "Deleting your account permanently removes your profile and every quote and application. This cannot be undone.",
  "danger.confirmLabel": "Type DELETE to confirm",
  "danger.submit": "Delete my account",
  "danger.deleting": "Deleting…",
  "danger.error.generic": "Could not delete account.",
  "danger.leaveTitle": "Leave team",
  "danger.leaveBody": "Leaving removes your access to this account. Your teammates and their data are unaffected.",
  "danger.leaveConfirmLabel": "Type LEAVE to confirm",
  "danger.leaveSubmit": "Leave this team",
  "danger.leaving": "Leaving…",
} as const;

export const es: Record<keyof typeof en, string> = {
  "account.breadcrumb": "Configuración · Cuenta",
  "account.title": "Cuenta",
  "account.subtitle": "Tu identidad, plan y datos. Los cambios aquí afectan cada documento que envías.",
  "you.title": "Tú",
  "you.help": "Cómo inicias sesión y cómo te llama la app. No se imprime en los documentos.",
  "signedIn.email": "Correo de inicio de sesión",
  "signedIn.manageBilling": "Gestionar facturación",
  "signedIn.logOut": "Cerrar sesión",
  "you.fullName": "Nombre completo",
  "you.fullNamePlaceholder": "Juan Constructor",
  "you.phone": "Tu teléfono",
  "you.phonePlaceholder": "(214) 555-0192",
  "you.phoneType": "Tipo de teléfono",
  "you.phoneType.placeholder": "Selecciona el tipo",
  "you.phoneType.mobile": "Móvil",
  "you.phoneType.office": "Oficina",
  "you.phoneType.home": "Casa",
  "you.save": "Guardar tus datos",
  "you.saved": "Tus datos se guardaron",
  "you.error.generic": "No se pudo guardar tu información.",
  "business.title": "Perfil del negocio",
  "business.help": "Esto se imprime en la parte superior de cada cotización y solicitud de pago. Las cotizaciones se prellenan desde aquí.",
  "business.name": "Nombre del negocio",
  "business.license": "N.° de licencia",
  "business.phone": "Teléfono del negocio",
  "business.email": "Correo de facturación",
  "business.street": "Dirección",
  "business.city": "Ciudad",
  "business.state": "Estado",
  "business.zip": "Código postal",
  "business.save": "Guardar información del negocio",
  "business.saved": "Perfil del negocio guardado",
  "business.error.generic": "No se pudo guardar el perfil del negocio.",
  "business.logo.label": "Logotipo del negocio",
  "business.logo.dropPrompt": "Arrastra un PNG aquí, o",
  "business.logo.chooseFile": "Elegir archivo",
  "business.logo.hint": "Un PNG transparente funciona mejor. Se imprime en la esquina superior derecha de cada documento.",
  "business.logo.pngOnly": "Solo PNG",
  "business.logo.replace": "Reemplazar",
  "business.logo.remove": "Quitar",
  "business.logo.removed": "Logotipo eliminado",
  "business.logo.uploading": "Subiendo {filename}",
  "business.logo.error.notPng": "Solo PNG — los JPG no admiten fondo transparente.",
  "business.logo.error.tooLarge": "Ese archivo supera los 2 MB. Intenta exportarlo más pequeño.",
  "business.logo.error.uploadFailed": "La subida no se completó. Revisa tu conexión e intenta de nuevo.",
  "business.logo.retry": "Intentar de nuevo",
  "markupDefault.title": "Valores predeterminados de cotización",
  "markupDefault.help": "El margen de materiales % con el que empieza cada cotización nueva, a menos que un cliente tenga su propia tasa guardada.",
  "markupDefault.label": "Margen predeterminado %",
  "markupDefault.save": "Guardar valor predeterminado",
  "markupDefault.saved": "Margen predeterminado guardado",
  "markupDefault.error.generic": "No se pudo guardar tu margen predeterminado.",
  "storage.title": "Almacenamiento",
  "storage.help": "Todo está sincronizado. Los documentos antiguos siguen siendo legibles aunque llegues al límite.",
  "export.title": "Exportar tus datos",
  "export.help": "Descarga todo lo de tu cuenta — documentos, clientes, contratos, catálogo y plantillas — en un solo archivo.",
  "export.cta": "Descargar mis datos",
  "export.exporting": "Exportando…",
  "export.error.generic": "No se pudieron exportar tus datos. Inténtalo de nuevo.",
  "plan.title": "Plan",
  "plan.body": "Cotizaciones y solicitudes de pago G702/G703 ilimitadas — hasta 3 cuentas de usuario.",
  "plan.freeTrial": "Prueba gratuita",
  "plan.daysLeftSingular": "{count} día restante",
  "plan.daysLeftPlural": "{count} días restantes",
  "plan.subscribeButton": "Suscribirme — {price}",
  "plan.discountPercent": "{percent}% DE DESCUENTO",
  "plan.discountAmount": "{amount} DE DESCUENTO",
  "plan.discountEndsOn": "El descuento termina el {date}",
  "plan.discountEndsRenews": "El descuento termina el {date} — se renueva a {price}",
  "plan.renews": "Renueva el {date}",
  "plan.cancelledAccessThrough": "Cancelado — acceso hasta el {date}",
  "plan.ownerOnly": "Pídele al propietario de la cuenta que administre la facturación.",
  "language.title": "Idioma",
  "language.help": "Se guarda en tu perfil y se aplica en cada pantalla y encabezado de PDF.",
  "theme.title": "Apariencia",
  "theme.dark": "Oscuro",
  "theme.light": "Claro",
  "theme.system": "Sistema",
  "theme.help": "Solo tuyo — se guarda en este dispositivo y no se comparte con tu equipo. Las cotizaciones y solicitudes de pago impresas siempre salen en negro sobre blanco.",
  "team.title": "Equipo",
  "team.help": "Hasta 3 personas pueden compartir esta cuenta — invita a compañeros por correo electrónico.",
  "team.you": "(Tú)",
  "team.role.owner": "Propietario",
  "team.role.member": "Miembro",
  "team.removeButton": "Quitar",
  "team.removeConfirm": "¿Quitar a {email} de tu equipo?",
  "team.pendingTitle": "Invitaciones pendientes",
  "team.pendingBadge": "Pendiente",
  "team.expiredBadge": "Vencida",
  "team.cancelButton": "Cancelar",
  "team.cancelConfirm": "¿Cancelar la invitación a {email}?",
  "team.capReached": "Tu cuenta alcanzó su límite de 3 miembros.",
  "team.inviteLabel": "Invitar por correo electrónico",
  "team.invitePlaceholder": "compañero@ejemplo.com",
  "team.inviteButton": "Enviar invitación",
  "team.inviteSent": "Invitación enviada",
  "team.inviteSentNoEmail": "Se creó la invitación, pero no se pudo enviar el correo — comparte el enlace de registro directamente.",
  "team.error.generic": "Algo salió mal. Inténtalo de nuevo.",
  "danger.title": "Zona de peligro",
  "danger.body": "Eliminar tu cuenta borra tu perfil y todas las cotizaciones y solicitudes de forma permanente. No se puede deshacer.",
  "danger.confirmLabel": "Escribe DELETE para confirmar",
  "danger.submit": "Eliminar mi cuenta",
  "danger.deleting": "Eliminando…",
  "danger.error.generic": "No se pudo eliminar la cuenta.",
  "danger.leaveTitle": "Salir del equipo",
  "danger.leaveBody": "Al salir perderás el acceso a esta cuenta. Tus compañeros y sus datos no se ven afectados.",
  "danger.leaveConfirmLabel": "Escribe LEAVE para confirmar",
  "danger.leaveSubmit": "Salir de este equipo",
  "danger.leaving": "Saliendo…",
};
